import { useMemo } from 'react';
import { ColumnFiltersState } from '@tanstack/react-table';
import { TableNullStateType } from '../components/TableNullState/TableNullState';

interface UseTableNullStateProps {
  columnFilters: ColumnFiltersState;
  filterIds: string[];
  defaultNullStateType: TableNullStateType;
  globalFilter?: string;
}

/**
 * Hook to determine the appropriate null state type for a table based on applied filters.
 *
 * @param columnFilters - The current column filters state
 * @param table - The react-table instance
 * @param filterIds - Array of column IDs to check for applied filters
 * @param defaultNullStateType - The default null state type when no filters are applied
 * @param globalFilter - The current global filter value (search field)
 * @returns The appropriate TableNullStateType
 */
export const useTableNullState = ({
  columnFilters,
  filterIds,
  defaultNullStateType,
  globalFilter,
}: UseTableNullStateProps): TableNullStateType => {
  return useMemo(() => {
    const hasFilterApplied = filterIds.some((id) => {
      const filter = columnFilters.find((f) => f.id === id);
      if (!filter) {
        return false;
      }

      const { value } = filter;

      if (Array.isArray(value)) {
        return value.length > 0;
      }

      if (value && typeof value === 'object') {
        const possibleDateValue = value as { startDate?: string; endDate?: string };
        return Object.values(possibleDateValue).some((v) => v !== undefined && v !== null && `${v}`.trim() !== '');
      }

      return value !== undefined && value !== null && `${value}`.trim() !== '';
    });

    // Check if there's a global filter (search field) applied
    const hasGlobalFilter = globalFilter && globalFilter !== '';

    if (hasGlobalFilter) {
      return TableNullStateType.NO_RESULTS_FROM_SEARCH;
    }
    if (hasFilterApplied) {
      return TableNullStateType.NO_RESULTS_FROM_FILTERS;
    }
    return defaultNullStateType;
  }, [columnFilters, filterIds, defaultNullStateType, globalFilter]);
};
